/**
 * A film: a short drawn story about one plan type, as a pure function of time.
 *
 * Every frame is drawn from nothing at its time `t` — no state is carried from the frame
 * before — so the page can play it, a scrub bar can seek it, and the recorder can step it
 * one frame at a time and get the same picture the page showed.
 *
 * **The stage is a square that every frame contains.** A film composes its action inside
 * a 900-unit square centered on the origin, and the frame — whatever its shape — is
 * scaled so the square fits it exactly. A wide frame shows more yard either side, a tall
 * one more sky and ground; the action, the captions and the end card are inside the
 * square and so inside every frame. That is how one film records for a phone held
 * upright, a square feed and a widescreen at once, with no composition of its own for
 * each.
 */

import { BOIL_RATE, Ink } from "./ink.js";
import type { Palette } from "./palette.js";
import type { Mix } from "./sound.js";

/** The side of the square every frame contains, in stage units. */
export const STAGE = 900;

/** What the site has to say for itself in a film: the name on the door, the price. */
export interface Site {
  brand: string;
  price: string;
  /** The address to send a viewer to, or empty when the build does not know it. */
  host: string;
}

/** The part of the stage a frame shows, in stage units: always contains the square. */
export interface View {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Frame {
  ink: Ink;
  t: number;
  view: View;
  site: Site;
}

/** One line of a film's story: when it happens and what happens, in words. */
export interface Beat {
  at: number;
  /** What the film says on screen at this beat, if anything. */
  caption?: string;
  /** What happens, for the page's description of the film and a screen reader. */
  action: string;
}

export interface Film {
  /** How long the film runs, in seconds, end card included. */
  duration: number;
  /** The film's name, for the page heading and a recording's file name. */
  title: string;
  slug: string;
  /** What the canvas is, said once: its accessible name. */
  summary: string;
  /** The story in one sentence, under the page's heading. */
  lead(site: Site): string;
  /** The story, beat by beat, in words: the page prints it beside the film. */
  beats(site: Site): readonly Beat[];
  draw(frame: Frame): void;
  /**
   * The soundtrack: every cue scheduled on the mix at the film's own seconds (`sound.ts`).
   * Read off the same timing the picture is drawn from, so a knock lands on the frame
   * that shows it.
   */
  score?(mix: Mix, site: Site): void;
}

/**
 * Draws a film at time `t` into a context `width` × `height` pixels: the paper, the
 * drafting grid, the film, and the shade at the frame's edge.
 */
export function renderFrame(
  ctx: CanvasRenderingContext2D,
  film: Film,
  t: number,
  width: number,
  height: number,
  palette: Palette,
  site: Site,
): void {
  const scale = Math.min(width, height) / STAGE;
  const halfW = width / scale / 2;
  const halfH = height / scale / 2;
  const view: View = { left: -halfW, right: halfW, top: -halfH, bottom: halfH };

  const sheets = paperFor(width, height, palette);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.drawImage(sheets.paper, 0, 0);
  ctx.setTransform(scale, 0, 0, scale, width / 2, height / 2);

  const clamped = Math.min(film.duration, Math.max(0, t));
  const ink = new Ink(ctx, palette, Math.floor(clamped * BOIL_RATE));
  film.draw({ ink, t: clamped, view, site });

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.drawImage(sheets.shade, 0, 0);
  ctx.restore();
}

interface Sheets {
  key: string;
  paper: CanvasImageSource;
  shade: CanvasImageSource;
}

let sheets: Sheets | undefined;

/**
 * The paper under every frame and the shade over it, drawn once per size and palette.
 * They do not move — the grid belongs to the stage, not to any camera — and a full-frame
 * gradient redrawn sixty times a second is most of a phone's frame for nothing.
 */
function paperFor(width: number, height: number, palette: Palette): Sheets {
  const key = `${width}x${height}:${palette.paper}:${palette.grid}:${palette.shadow}`;
  if (sheets?.key === key) {
    return sheets;
  }
  const make = (): [CanvasImageSource, CanvasRenderingContext2D] => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (context === null) {
      throw new Error("no 2D context for the film's paper");
    }
    return [canvas, context];
  };
  const scale = Math.min(width, height) / STAGE;
  const halfW = width / scale / 2;
  const halfH = height / scale / 2;
  const [paper, paperCtx] = make();
  paperCtx.fillStyle = palette.paper;
  paperCtx.fillRect(0, 0, width, height);
  paperCtx.setTransform(scale, 0, 0, scale, width / 2, height / 2);
  drawGrid(paperCtx, { left: -halfW, right: halfW, top: -halfH, bottom: halfH }, palette);

  // A soft shade toward the edges, as a sheet of paper under a desk lamp.
  const [shade, shadeCtx] = make();
  const gradient = shadeCtx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.45,
    width / 2,
    height / 2,
    Math.hypot(width, height) * 0.62,
  );
  gradient.addColorStop(0, transparent(palette.shadow));
  gradient.addColorStop(1, withAlpha(palette.shadow, 0.5));
  shadeCtx.fillStyle = gradient;
  shadeCtx.fillRect(0, 0, width, height);
  sheets = { key, paper, shade };
  return sheets;
}

/** Drafting paper: a fine grid, with every fifth line heavier. */
function drawGrid(ctx: CanvasRenderingContext2D, view: View, palette: Palette): void {
  const step = 30;
  ctx.strokeStyle = palette.grid;
  for (const [every, width, alpha] of [
    [1, 1, 0.55],
    [5, 1.6, 0.8],
  ] as const) {
    ctx.lineWidth = width;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    const spacing = step * every;
    for (let x = Math.ceil(view.left / spacing) * spacing; x <= view.right; x += spacing) {
      ctx.moveTo(x, view.top);
      ctx.lineTo(x, view.bottom);
    }
    for (let y = Math.ceil(view.top / spacing) * spacing; y <= view.bottom; y += spacing) {
      ctx.moveTo(view.left, y);
      ctx.lineTo(view.right, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** A token's color at no opacity, for the clear end of a gradient. */
function transparent(color: string): string {
  return withAlpha(color, 0);
}

/**
 * A token's color at an opacity. A canvas gradient's stops take a color each, with no
 * `globalAlpha` of their own, so a token written as hex is spelled out with an alpha.
 */
export function withAlpha(color: string, alpha: number): string {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (hex === null) {
    return "transparent";
  }
  const digits = hex[1]!.length === 3 ? [...hex[1]!].map((d) => d + d).join("") : hex[1]!;
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(digits.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
